import React, { useMemo } from "react";
import { ReactNode } from "react";
import { KeywordInputType, QueryField, QueryInfo, fieldIsKeyword, getKeywordInputType } from "system/query/QueryInfo";
import { Box, MenuItem, Select, SelectChangeEvent, TextField } from "@mui/material";
import { QueryDynamicOptionsInput } from "./QueryDynamicKeywordInput";
import { useTranslation } from "react-i18next";
import { QueryFixedKeywordInput } from "./QueryFixedKeywordInput";
import { QueryDateSelector } from "./QueryDateSelector";
import { queryOps } from "system/query/Query";

export interface QueryValueInputProps {
  queryInfo: QueryInfo;
  queryOp?: queryOps;

  isInline?: boolean;
  isFocused?: boolean;

  selectedField: QueryField | undefined;

  value: string | number | boolean | null | undefined;
  setValue: (newValue: string | number | boolean | null | undefined, newValueLabel?: string | undefined) => void;
  label?: string;
  setLabel?: (label: string) => void;
  setLabels?: (labels: string[]) => void;

  keyWasPressed?: (event: React.KeyboardEvent<HTMLInputElement>) => void;

  useSimpifiedRelativeDates?: boolean;

  enforceSelection?: boolean;
  excludeOptions?: string[];

  children?: ReactNode;
  /**
   * Determines if the selected date is inclusive of the selected date (starts at the beginning of the day).
   * @default false
   */
  isDateInclusive?: boolean;
  includeNowOption?: boolean;
}

export function QueryValueInput(props: QueryValueInputProps): JSX.Element {
  const { i18n } = useTranslation();

  const keywordInputType = useMemo(() => {
    if (props.selectedField && fieldIsKeyword(props.selectedField)) {
      return getKeywordInputType(props.selectedField);
    }
  }, [props.selectedField]);

  return (
    <Box sx={{display: props.isInline ? "inline" : "block"}}>
    {props.selectedField && (props.selectedField.type === "text" ||
      (keywordInputType === KeywordInputType.string)) &&
      <TextField
        autoComplete="off"
        variant="outlined"
        value={props.value || ""}
        autoFocus={props.isFocused}
        onKeyDown={(event: React.KeyboardEvent<HTMLInputElement>) => {
          if (props.keyWasPressed) {
            props.keyWasPressed(event);
          }
        }}
        onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
          props.setValue(event.target.value);
        }}
      />
    }
    {props.selectedField &&
      (props.selectedField.type === "integer" || props.selectedField.type === "decimal" ||
        keywordInputType === KeywordInputType.numeric) &&
      <TextField
        sx={{ width: "100%" }}
        autoComplete="off"
        variant="outlined"
        type="number"
        value={props.value ?? ""}
        autoFocus={props.isFocused}
        onKeyDown={(event: React.KeyboardEvent<HTMLInputElement>) => {
          if (props.keyWasPressed) {
            props.keyWasPressed(event);
          }
        }}
        onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
          if (!props.selectedField) {
            return;
          }
          if (!event.target.value) {
            props.setValue(event.target.value);
            return;
          }
          let value = Number(event.target.value);
          if (props.selectedField.type === "integer") {
            value = Math.floor(value);
          }
          props.setValue(value);
        }}
      />
    }

    {keywordInputType === KeywordInputType.options &&
      <QueryFixedKeywordInput {...props}
        setValue={(value: string | number | boolean | null | undefined, label?: string) => {
          props.setValue(value);
          if (props.setLabels) {
            props.setLabels([label || ""]);
          }
        }}/>
    }
    {keywordInputType === KeywordInputType.autocomplete &&
      <QueryDynamicOptionsInput {...props} />
    }
    {props.selectedField && props.selectedField.type === "boolean" &&
      <Select
        value={(props.value ? 1 : 0) + ""}
        onChange={(event: SelectChangeEvent) => {
          props.setValue(!!event.target.value);
        }} >
        <MenuItem value={1}>{i18n.t("true")}</MenuItem>
        <MenuItem value={0}>{i18n.t("false")}</MenuItem>
      </Select>
    }
    {props.selectedField && props.selectedField.type === "timestamp" &&
      <QueryDateSelector
        includeNowOption={props.includeNowOption}
        disableRelativeDates={props.queryOp === queryOps.contains}
        useSimpifiedRelativeDates={props.useSimpifiedRelativeDates}
        onChange={(value: string) => {
          props.setValue(value);
        }}
        value={props.value as string}
        isDateInclusive={props.isDateInclusive}
      />
    }
    {!props.selectedField &&
      <></>
    }
    </Box>
  );
}
